import React, { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '../config';
import { useLanguage } from '../contexts/LanguageContext';

function InboundInvoices({ theme }) {
    const { t } = useLanguage();
    const [inventory, setInventory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        purchase_date: '',
        price: '',
        iva: ''
    });
    const [importing, setImporting] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState('purchase_date');
    const [sortDirection, setSortDirection] = useState('desc');

    const fetchInventory = useCallback(async () => {
        try {
            setLoading(true);
            const response = await fetch(`${BACKEND_URL}/api/admin/inventory`);
            if (!response.ok) throw new Error(t('failedToFetchInventory'));
            const data = await response.json();
            setInventory(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchInventory();
    }, [fetchInventory]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const url = editingItem
                ? `${BACKEND_URL}/api/admin/inventory/${editingItem.id}`
                : `${BACKEND_URL}/api/admin/inventory`;
            const method = editingItem ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });

            if (!response.ok) throw new Error(t('failedToSaveInventoryItem'));

            await fetchInventory();
            resetForm();
        } catch (err) {
            alert(t('error') + ': ' + err.message);
        }
    };

    const handleEdit = (item) => {
        setEditingItem(item);
        setFormData({
            name: item.name,
            purchase_date: item.purchase_date ? item.purchase_date.split('T')[0] : '',
            price: item.price,
            iva: item.iva
        });
        setShowAddForm(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('confirmDeleteInventoryItem'))) return;

        try {
            const response = await fetch(`${BACKEND_URL}/api/admin/inventory/${id}`, {
                method: 'DELETE'
            });
            if (!response.ok) throw new Error(t('failedToDeleteInventoryItem'));
            await fetchInventory();
        } catch (err) {
            alert(t('error') + ': ' + err.message);
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            purchase_date: '',
            price: '',
            iva: ''
        });
        setEditingItem(null);
        setShowAddForm(false);
    };

    const formatDate = (dateString) => {
        if (!dateString) return t('notApplicable');
        return new Date(dateString).toLocaleDateString();
    };

    const formatCurrency = (amount) => {
        return `$${Number(amount).toFixed(2)}`;
    };

    // Filter inventory based on search term
    const matchesSearch = (item) => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        return [
            item.name,
            item.purchase_date ? formatDate(item.purchase_date) : '',
            item.price?.toString(),
            item.iva?.toString(),
            (Number(item.price || 0) + Number(item.iva || 0)).toFixed(2)
        ].some(field =>
            field && field.toString().toLowerCase().includes(search)
        );
    };

    const filteredInventory = inventory.filter(matchesSearch);

    // Apply sorting
    const sortedInventory = filteredInventory.sort((a, b) => {
        let aValue, bValue;

        switch (sortField) {
            case 'name':
                aValue = (a.name || '').toLowerCase();
                bValue = (b.name || '').toLowerCase();
                break;
            case 'purchase_date':
                aValue = new Date(a.purchase_date).getTime();
                bValue = new Date(b.purchase_date).getTime();
                break;
            case 'price':
                aValue = parseFloat(a.price) || 0;
                bValue = parseFloat(b.price) || 0;
                break;
            case 'iva':
                aValue = parseFloat(a.iva) || 0;
                bValue = parseFloat(b.iva) || 0;
                break;
            case 'total':
                aValue = parseFloat(a.price || 0) + parseFloat(a.iva || 0);
                bValue = parseFloat(b.price || 0) + parseFloat(b.iva || 0);
                break;
            default:
                aValue = a[sortField];
                bValue = b[sortField];
        }

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
    });

    const handleExport = () => {
        try {
            // Create CSV content
            const headers = [t('name'), t('purchaseDate'), t('price'), t('iva'), t('total')];
            const csvContent = [
                headers.join(','),
                ...inventory.map(item => [
                    `"${item.name}"`,
                    item.purchase_date ? new Date(item.purchase_date).toLocaleDateString() : '',
                    item.price || '0',
                    item.iva || '0',
                    (Number(item.price || 0) + Number(item.iva || 0)).toFixed(2)
                ].join(','))
            ].join('\n');

            // Create and download file
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `inventory_export_${new Date().toISOString().split('T')[0]}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            alert(t('errorExportingInventory') + ': ' + err.message);
        }
    };

    const handleImport = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setImporting(true);
        try {
            const text = await file.text();
            const lines = text.split('\n');
            const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim());

            // Validate headers
            const expectedHeaders = [t('name'), t('purchaseDate'), t('price'), t('iva'), t('total')];
            const hasValidHeaders = expectedHeaders.slice(0, 4).every(header =>
                headers.some(h => h.toLowerCase().includes(header.toLowerCase()))
            );

            if (!hasValidHeaders) {
                throw new Error(t('invalidCsvFormat'));
            }

            const itemsToImport = [];
            for (let i = 1; i < lines.length; i++) {
                const line = lines[i].trim();
                if (!line) continue;

                const values = line.split(',').map(v => v.replace(/"/g, '').trim());
                if (values.length >= 4) {
                    const purchaseDate = values[1] ? new Date(values[1]).toISOString().split('T')[0] : '';
                    itemsToImport.push({
                        name: values[0],
                        purchase_date: purchaseDate,
                        price: parseFloat(values[2]) || 0,
                        iva: parseFloat(values[3]) || 0
                    });
                }
            }

            // Import items one by one
            let importedCount = 0;
            for (const item of itemsToImport) {
                try {
                    const response = await fetch(`${BACKEND_URL}/api/admin/inventory`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(item)
                    });
                    if (response.ok) importedCount++;
                } catch (err) {
                    console.error('Error importing item:', item.name, err);
                }
            }

            alert(t('successfullyImported').replace('{count}', importedCount).replace('{total}', itemsToImport.length));
            await fetchInventory(); // Refresh the inventory list
        } catch (err) {
            alert(t('errorImportingInventory') + ': ' + err.message);
        } finally {
            setImporting(false);
            // Reset file input
            event.target.value = '';
        }
    };

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    if (loading) return <div>{t('loadingInventory')}...</div>;
    if (error) return <div style={{ color: 'red' }}>{t('error')}: {error}</div>;

    return (
        <div style={{
            background: theme.colors.surface,
            padding: '0rem',
            borderRadius: theme.borderRadius || 8,
            boxShadow: theme.boxShadow,
            marginBottom: '1.5rem'
        }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem'
            }}>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                        onClick={handleExport}
                        style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: theme.colors.success,
                            color: theme.colors.surface,
                            border: 'none',
                            borderRadius: theme.borderRadius || 8,
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            fontSize: '0.9rem',
                            boxShadow: theme.boxShadow
                        }}
                    >
                        📤 {t('export')}
                    </button>
                    <label style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: theme.colors.warning,
                        color: theme.colors.surface,
                        border: 'none',
                        borderRadius: theme.borderRadius || 8,
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '0.9rem',
                        boxShadow: theme.boxShadow,
                        display: 'flex',
                        alignItems: 'center'
                    }}>
                        {importing ? '⏳ ' + t('importing') + '...' : '📥 ' + t('import')}
                        <input
                            type="file"
                            accept=".csv"
                            onChange={handleImport}
                            disabled={importing}
                            style={{ display: 'none' }}
                        />
                    </label>
                    <button
                        onClick={() => setShowAddForm(!showAddForm)}
                        style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: theme.colors.primary,
                            color: theme.colors.surface,
                            border: 'none',
                            borderRadius: theme.borderRadius || 8,
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            fontSize: '0.9rem',
                            boxShadow: theme.boxShadow
                        }}
                    >
                        {showAddForm ? t('cancel') : t('addItem')}
                    </button>
                </div>
            </div>

            {showAddForm && (
                <form onSubmit={handleSubmit} style={{
                    background: theme.colors.background,
                    padding: '1rem',
                    borderRadius: theme.borderRadius || 8,
                    marginBottom: '1rem',
                    border: `1px solid ${theme.colors.primary}20`
                }}>
                    <h3 style={{ color: theme.colors.primary, margin: '0 0 1rem 0' }}>
                        {editingItem ? t('editInventoryItem') : t('addNewInventoryItem')}
                    </h3>

                    {/* Top Button Section */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '1rem',
                        marginBottom: '1rem'
                    }}>
                        <button
                            type="button"
                            onClick={resetForm}
                            style={{
                                padding: '0.75rem 1.5rem',
                                backgroundColor: theme?.colors?.error || '#f44336',
                                color: theme?.colors?.surface || '#fff',
                                border: 'none',
                                borderRadius: theme?.borderRadius || 8,
                                cursor: 'pointer',
                                fontSize: '1rem',
                                fontWeight: 'bold'
                            }}
                        >
                            {t('cancel')}
                        </button>
                        <button
                            type="submit"
                            disabled={!formData.name || !formData.purchase_date || !formData.price || !formData.iva}
                            style={{
                                padding: '0.75rem 1.5rem',
                                backgroundColor: (!formData.name || !formData.purchase_date || !formData.price || !formData.iva) ? '#ccc' : (theme?.colors?.success || '#4caf50'),
                                color: theme?.colors?.surface || '#fff',
                                border: 'none',
                                borderRadius: theme?.borderRadius || 8,
                                cursor: (!formData.name || !formData.purchase_date || !formData.price || !formData.iva) ? 'not-allowed' : 'pointer',
                                fontSize: '1rem',
                                fontWeight: 'bold',
                                opacity: (!formData.name || !formData.purchase_date || !formData.price || !formData.iva) ? 0.6 : 1
                            }}
                        >
                            {editingItem ? t('update') : t('add')} {t('item')}
                        </button>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                {t('name')}:
                            </label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({...formData, name: e.target.value})}
                                required
                                style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    borderRadius: theme.borderRadius || 4,
                                    border: `1px solid ${theme.colors.primary}40`
                                }}
                            />
                        </div>

                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                {t('purchaseDate')}:
                            </label>
                            <input
                                type="date"
                                value={formData.purchase_date}
                                onChange={(e) => setFormData({...formData, purchase_date: e.target.value})}
                                required
                                style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    borderRadius: theme.borderRadius || 4,
                                    border: `1px solid ${theme.colors.primary}40`
                                }}
                            />
                        </div>

                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                {t('price')}:
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                value={formData.price}
                                onChange={(e) => setFormData({...formData, price: e.target.value})}
                                onWheel={(e) => e.target.blur()}
                                required
                                style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    borderRadius: theme.borderRadius || 4,
                                    border: `1px solid ${theme.colors.primary}40`
                                }}
                            />
                        </div>

                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                {t('iva')}:
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                value={formData.iva}
                                onChange={(e) => setFormData({...formData, iva: e.target.value})}
                                onWheel={(e) => e.target.blur()}
                                required
                                style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    borderRadius: theme.borderRadius || 4,
                                    border: `1px solid ${theme.colors.primary}40`
                                }}
                            />
                        </div>
                    </div>

                    {/* Bottom Button Section */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '1rem'
                    }}>
                        <button
                            type="button"
                            onClick={resetForm}
                            style={{
                                padding: '0.75rem 1.5rem',
                                backgroundColor: theme?.colors?.error || '#f44336',
                                color: theme?.colors?.surface || '#fff',
                                border: 'none',
                                borderRadius: theme?.borderRadius || 8,
                                cursor: 'pointer',
                                fontSize: '1rem',
                                fontWeight: 'bold'
                            }}
                        >
                            {t('cancel')}
                        </button>
                        <button
                            type="submit"
                            disabled={!formData.name || !formData.purchase_date || !formData.price || !formData.iva}
                            style={{
                                padding: '0.75rem 1.5rem',
                                backgroundColor: (!formData.name || !formData.purchase_date || !formData.price || !formData.iva) ? '#ccc' : (theme?.colors?.success || '#4caf50'),
                                color: theme?.colors?.surface || '#fff',
                                border: 'none',
                                borderRadius: theme?.borderRadius || 8,
                                cursor: (!formData.name || !formData.purchase_date || !formData.price || !formData.iva) ? 'not-allowed' : 'pointer',
                                fontSize: '1rem',
                                fontWeight: 'bold',
                                opacity: (!formData.name || !formData.purchase_date || !formData.price || !formData.iva) ? 0.6 : 1
                            }}
                        >
                            {editingItem ? t('update') : t('add')} {t('item')}
                        </button>
                    </div>
                </form>
            )}

            {/* Search Input */}
            <div style={{ marginBottom: '1rem' }}>
                <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={t('searchInvoices')}
                    style={{
                        width: '100%',
                        padding: '0.75rem',
                        fontSize: '0.95rem',
                        border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                        borderRadius: theme?.borderRadius || 4,
                        backgroundColor: theme?.colors?.surface || '#fff',
                        color: theme?.colors?.text || '#000',
                        outline: 'none'
                    }}
                    onFocus={(e) => {
                        e.target.style.borderColor = theme?.colors?.primary || '#1976d2';
                    }}
                    onBlur={(e) => {
                        e.target.style.borderColor = theme?.colors?.border || '#ddd';
                    }}
                />
                {searchTerm && (
                    <div style={{
                        marginTop: '0.5rem',
                        fontSize: '0.85rem',
                        color: theme?.colors?.textSecondary || '#666'
                    }}>
                        {t('showing')} {filteredInventory.length} {t('of')} {inventory.length} {t('items').toLowerCase()}
                    </div>
                )}
            </div>

            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {sortedInventory.length === 0 ? (
                    <p style={{ color: theme.colors.textSecondary, textAlign: 'center' }}>
                        {t('noInventoryItemsFound')}
                    </p>
                ) : (
                    <table style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: '0.9rem'
                    }}>
                        <thead>
                            <tr style={{ borderBottom: `2px solid ${theme.colors.primary}` }}>
                                <th
                                    onClick={() => handleSort('name')}
                                    style={{
                                        padding: '0.75rem',
                                        textAlign: 'left',
                                        color: theme.colors.primary,
                                        cursor: 'pointer',
                                        userSelect: 'none'
                                    }}
                                >
                                    {t('name')} {sortField === 'name' && (sortDirection === 'asc' ? '▲' : '▼')}
                                </th>
                                <th
                                    onClick={() => handleSort('purchase_date')}
                                    style={{
                                        padding: '0.75rem',
                                        textAlign: 'left',
                                        color: theme.colors.primary,
                                        cursor: 'pointer',
                                        userSelect: 'none'
                                    }}
                                >
                                    {t('purchaseDate')} {sortField === 'purchase_date' && (sortDirection === 'asc' ? '▲' : '▼')}
                                </th>
                                <th
                                    onClick={() => handleSort('price')}
                                    style={{
                                        padding: '0.75rem',
                                        textAlign: 'right',
                                        color: theme.colors.primary,
                                        cursor: 'pointer',
                                        userSelect: 'none'
                                    }}
                                >
                                    {t('price')} {sortField === 'price' && (sortDirection === 'asc' ? '▲' : '▼')}
                                </th>
                                <th
                                    onClick={() => handleSort('iva')}
                                    style={{
                                        padding: '0.75rem',
                                        textAlign: 'right',
                                        color: theme.colors.primary,
                                        cursor: 'pointer',
                                        userSelect: 'none'
                                    }}
                                >
                                    {t('iva')} {sortField === 'iva' && (sortDirection === 'asc' ? '▲' : '▼')}
                                </th>
                                <th
                                    onClick={() => handleSort('total')}
                                    style={{
                                        padding: '0.75rem',
                                        textAlign: 'right',
                                        color: theme.colors.primary,
                                        cursor: 'pointer',
                                        userSelect: 'none'
                                    }}
                                >
                                    {t('total')} {sortField === 'total' && (sortDirection === 'asc' ? '▲' : '▼')}
                                </th>
                                <th style={{ padding: '0.75rem', textAlign: 'center', color: theme.colors.primary }}>{t('actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedInventory.map(item => (
                                <tr key={item.id} style={{
                                    borderBottom: `1px solid ${theme.colors.primary}20`
                                }}>
                                    <td style={{ padding: '0.75rem' }}>{item.name}</td>
                                    <td style={{ padding: '0.75rem' }}>{formatDate(item.purchase_date)}</td>
                                    <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrency(item.price)}</td>
                                    <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrency(item.iva)}</td>
                                    <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold' }}>
                                        {formatCurrency(Number(item.price) + Number(item.iva))}
                                    </td>
                                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                        <button
                                            onClick={() => handleEdit(item)}
                                            style={{
                                                marginRight: '0.5rem',
                                                padding: '0.25rem 0.5rem',
                                                backgroundColor: theme.colors.primary,
                                                color: theme.colors.surface,
                                                border: 'none',
                                                borderRadius: theme.borderRadius || 4,
                                                cursor: 'pointer',
                                                fontSize: '0.8rem'
                                            }}
                                        >
                                            {t('edit')}
                                        </button>
                                        <button
                                            onClick={() => handleDelete(item.id)}
                                            style={{
                                                padding: '0.25rem 0.5rem',
                                                backgroundColor: '#f44336',
                                                color: theme.colors.surface,
                                                border: 'none',
                                                borderRadius: theme.borderRadius || 4,
                                                cursor: 'pointer',
                                                fontSize: '0.8rem'
                                            }}
                                        >
                                            {t('delete')}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

export default InboundInvoices;
